import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { API, api } from "@/lib/api";
import { MapPin, Mail, Phone, ChevronLeft, ChevronRight, Building2, BedDouble, Bath, Ruler, CalendarClock, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const protectImage = (event) => {
  if (event.target instanceof Element && event.target.closest("img, video")) event.preventDefault();
};

export default function PropertyWebsite() {
  const { slug } = useParams();
  const preview = new URLSearchParams(useLocation().search).get("preview") === "1";
  const [listing, setListing] = useState(null);
  const [error, setError] = useState("");
  const [heroIndex, setHeroIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showingOpen, setShowingOpen] = useState(false);

  useEffect(() => {
    const request = preview ? api.get(`/admin/delivery-projects/${slug}/website-preview`) : api.get(`/public/listing/${slug}`);
    request.then((response) => setListing(response.data)).catch(() => setError(preview ? "Preview is unavailable. Sign in as an admin to continue." : "This property website is not available."));
  }, [preview, slug]);

  const images = useMemo(() => (listing?.files || []).filter((file) => file.is_image), [listing]);
  const ordered = useMemo(() => {
    if (!images.length) return [];
    const cover = images.find((file) => file.id === listing?.cover_file_id);
    return cover ? [cover, ...images.filter((file) => file.id !== cover.id)] : images;
  }, [images, listing]);
  const fileUrl = (file) => `${API}/public/delivery-galleries/${slug}/files/${file.id}`;

  useEffect(() => {
    if (!listing) return undefined;
    document.title = `${listing.project_name || "Property"} | Mandalay Real Estate Media`;
    if (lightboxIndex !== null || showingOpen) return undefined;
    const interval = setInterval(() => setHeroIndex((i) => (ordered.length ? (i + 1) % ordered.length : 0)), 7000);
    return () => clearInterval(interval);
  }, [listing, ordered.length, lightboxIndex, showingOpen]);

  if (error) return <main data-testid="public-listing-error" className="min-h-screen bg-[#070A12] text-white grid place-items-center p-8 text-center"><div><div className="font-serif-display text-3xl italic">Mandalay</div><h1 className="mt-8 text-2xl font-serif-display">{error}</h1></div></main>;
  if (!listing) return <main data-testid="public-listing-loading" className="min-h-screen grid place-items-center bg-[#070A12] text-white font-mono text-sm">Loading property website…</main>;

  const contact = listing.contact;
  const hero = ordered[heroIndex];
  const stats = [
    listing.beds && { icon: BedDouble, label: "Beds", value: listing.beds },
    listing.baths && { icon: Bath, label: "Baths", value: listing.baths },
    listing.sqft && { icon: Ruler, label: "Sq Ft", value: listing.sqft },
  ].filter(Boolean);

  return (
    <main data-testid="public-property-website" onContextMenuCapture={protectImage} onDragStartCapture={protectImage} className="min-h-screen bg-[#070A12] text-white">
      {preview && <div data-testid="public-listing-preview-banner" className="bg-[#C5A059] text-[#0B0F1A] text-center text-xs font-mono uppercase tracking-[0.14em] py-2">Admin preview — this is how the page will look to visitors</div>}

      <div className="flex flex-col lg:flex-row">
      {contact && (
        <aside className="order-2 lg:order-1 lg:w-[380px] xl:w-[420px] shrink-0 lg:h-screen lg:sticky lg:top-0 lg:overflow-y-auto bg-[#0A0E18] border-t lg:border-t-0 lg:border-r border-[#C5A059]/15 flex flex-col">
          <div className="flex-1 flex flex-col items-center text-center px-8 sm:px-10 pt-14 pb-10">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C5A059]/80">Presented By</p>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="mt-6 relative">
              <div className="absolute inset-0 rounded-full blur-xl bg-[#C5A059]/20 scale-110" />
              {contact.photo_url
                ? <img src={contact.photo_url} alt={contact.name} className="relative h-32 w-32 rounded-full object-cover ring-2 ring-[#C5A059]/60" />
                : <span className="relative h-32 w-32 rounded-full bg-[#15203A] ring-2 ring-[#C5A059]/40 grid place-items-center text-3xl font-serif-display">{(contact.name || "?").charAt(0)}</span>}
            </motion.div>
            <h2 data-testid="public-listing-agent-name" className="mt-6 font-serif-display text-2xl">{contact.name}</h2>
            {contact.company && <p className="mt-1.5 flex items-center gap-1.5 text-sm text-white/55"><Building2 size={14} className="text-[#C5A059]" />{contact.company}</p>}
            <div className="w-10 h-px bg-[#C5A059]/40 my-7" />
            <div className="w-full space-y-3">
              {contact.phone && <a data-testid="public-listing-contact-call" href={`tel:${contact.phone}`} className="flex items-center justify-center gap-2 w-full rounded-full bg-[#C5A059] text-[#0B0F1A] text-xs font-semibold uppercase tracking-[0.1em] py-3 hover:bg-[#d4b370] transition-colors"><Phone size={14} />Call {contact.name?.split(" ")[0]}</a>}
              {contact.email && <a data-testid="public-listing-contact-email" href={`mailto:${contact.email}`} className="flex items-center justify-center gap-2 w-full rounded-full border border-white/20 text-xs font-semibold uppercase tracking-[0.1em] py-3 hover:bg-white/5 transition-colors"><Mail size={14} />Send a Message</a>}
            </div>
            {contact.logo_url && <img src={contact.logo_url} alt={contact.company} className="mt-10 h-7 max-w-[140px] object-contain opacity-80" />}
          </div>
          <div className="px-8 py-6 border-t border-white/5 text-center">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30">Mandalay Real Estate Media</p>
          </div>
        </aside>
      )}

      <section className="order-1 lg:order-2 flex-1 min-w-0">
        <div className="sticky top-0 z-20 bg-[#070A12]/85 backdrop-blur-md border-b border-white/5 px-6 sm:px-10 py-5 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 data-testid="public-listing-title" className="font-serif-display text-xl sm:text-2xl truncate">{listing.project_name}</h1>
            {listing.full_address && <p data-testid="public-listing-address" className="mt-1 flex items-center gap-1.5 text-xs sm:text-sm text-white/55"><MapPin size={13} className="text-[#C5A059] shrink-0" />{listing.full_address}</p>}
          </div>
          <div className="flex items-center gap-5 sm:gap-7 shrink-0">
            {stats.map((s) => (
              <div key={s.label} data-testid={`public-listing-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="text-center">
                <div className="flex items-center justify-center gap-1.5 font-serif-display text-lg sm:text-xl"><s.icon size={15} className="text-[#C5A059]" />{s.value}</div>
                <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-white/40">{s.label}</div>
              </div>
            ))}
            {listing.price && <div data-testid="public-listing-price" className="font-serif-display text-xl sm:text-2xl text-[#C5A059] pl-2 sm:pl-4 sm:border-l border-white/10">{listing.price}</div>}
          </div>
        </div>

        <div className="relative h-[52vh] sm:h-[72vh] w-full overflow-hidden bg-[#0C2347]">
          <AnimatePresence mode="wait" initial={false}>
            {hero && (
              <motion.img key={hero.id} src={fileUrl(hero)} alt={listing.project_name} onClick={() => setLightboxIndex(heroIndex)}
                initial={{ opacity: 0, scale: 1.0 }} animate={{ opacity: 1, scale: 1.08 }} exit={{ opacity: 0 }} transition={{ opacity: { duration: 1 }, scale: { duration: 7, ease: "linear" } }}
                className="absolute inset-0 h-full w-full object-cover cursor-pointer" style={{ transformStyle: "preserve-3d" }} data-testid="public-listing-hero-image" />
            )}
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-[#070A12] via-transparent to-transparent opacity-80 pointer-events-none" />
          {ordered.length > 1 && (
            <>
              <button data-testid="public-listing-hero-prev" onClick={() => setHeroIndex((i) => (i - 1 + ordered.length) % ordered.length)} className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 grid place-items-center backdrop-blur-sm transition-colors"><ChevronLeft size={18} /></button>
              <button data-testid="public-listing-hero-next" onClick={() => setHeroIndex((i) => (i + 1) % ordered.length)} className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/30 hover:bg-black/50 grid place-items-center backdrop-blur-sm transition-colors"><ChevronRight size={18} /></button>
              <div className="absolute bottom-5 inset-x-0 flex items-center justify-center gap-1.5">
                {ordered.map((f, i) => (
                  <button key={f.id} data-testid={`public-listing-hero-dot-${i}`} onClick={() => setHeroIndex(i)} className={`h-1.5 rounded-full transition-all ${i === heroIndex ? "w-6 bg-[#C5A059]" : "w-1.5 bg-white/40 hover:bg-white/60"}`} />
                ))}
              </div>
            </>
          )}
          <button data-testid="public-listing-schedule-showing" onClick={() => setShowingOpen(true)} className="absolute bottom-16 sm:bottom-7 left-5 sm:left-7 flex items-center gap-2 rounded-full bg-black/35 hover:bg-black/55 backdrop-blur-md border border-[#C5A059]/50 text-white text-xs font-mono uppercase tracking-[0.12em] px-4 py-2.5 sm:px-5 sm:py-3 transition-colors">
            <CalendarClock size={14} className="text-[#C5A059]" />Schedule a Showing
          </button>
        </div>

        {listing.video && (
          <section className={`max-w-5xl mx-auto px-6 sm:px-10 pt-14 sm:pt-20 ${ordered.length > 1 ? "" : "pb-14 sm:pb-20"}`}>
            <h2 className="font-serif-display text-xl sm:text-2xl text-center mb-6 text-white/90">Walkthrough Video</h2>
            <div data-testid="public-listing-video" className="relative aspect-video rounded-2xl overflow-hidden ring-1 ring-[#C5A059]/15 bg-black">
              {listing.video.kind === "embed"
                ? <iframe data-testid="public-listing-video-embed" src={listing.video.embed_url} title="Walkthrough video" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full border-0" />
                : <video data-testid="public-listing-video-file" controls playsInline className="absolute inset-0 w-full h-full object-contain bg-black" src={`${API}/public/delivery-galleries/${slug}/files/${listing.video.file_id}`} />}
            </div>
          </section>
        )}

        {ordered.length > 1 && (
          <section className="max-w-6xl mx-auto px-6 sm:px-10 py-14 sm:py-20">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5" style={{ perspective: "1200px" }}>
              {ordered.slice(1).map((file, i) => (
                <TiltPhoto key={file.id} testId={`public-listing-photo-${file.id}`} src={fileUrl(file)} index={i} onClick={() => setLightboxIndex(i + 1)} />
              ))}
            </div>
          </section>
        )}

        <footer className="border-t border-white/5 py-8 text-center">
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-white/30">Captured &amp; delivered by Mandalay Real Estate Media</p>
        </footer>
      </section>
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox photos={ordered.map((f) => ({ id: f.id, url: fileUrl(f) }))} index={lightboxIndex} onIndexChange={setLightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>

      <ShowingRequestDialog open={showingOpen} onClose={() => setShowingOpen(false)} slug={slug} agentName={contact?.name} address={listing.full_address || listing.project_name} />
    </main>
  );
}

function Lightbox({ photos, index, onIndexChange, onClose }) {
  const touchStartX = useRef(null);
  const photo = photos[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndexChange((index + 1) % photos.length);
      else if (e.key === "ArrowLeft") onIndexChange((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onIndexChange, onClose]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) onIndexChange((index + (delta < 0 ? 1 : -1) + photos.length) % photos.length);
    touchStartX.current = null;
  };

  if (!photo) return null;
  return (
    <motion.div data-testid="public-listing-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
      onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      <button data-testid="public-listing-lightbox-close" onClick={onClose} className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center z-10 text-white"><X size={20} /></button>
      {photos.length > 1 && (
        <button data-testid="public-listing-lightbox-prev" onClick={(e) => { e.stopPropagation(); onIndexChange((index - 1 + photos.length) % photos.length); }} className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center z-10 text-white"><ChevronLeft size={22} /></button>
      )}
      {photos.length > 1 && (
        <button data-testid="public-listing-lightbox-next" onClick={(e) => { e.stopPropagation(); onIndexChange((index + 1) % photos.length); }} className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center z-10 text-white"><ChevronRight size={22} /></button>
      )}
      <AnimatePresence mode="wait">
        <motion.img key={photo.id} src={photo.url} alt="" onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
          className="max-h-[88vh] max-w-[92vw] object-contain select-none" data-testid="public-listing-lightbox-image" />
      </AnimatePresence>
      {photos.length > 1 && <div className="absolute bottom-5 inset-x-0 text-center text-white/50 text-xs font-mono">{index + 1} / {photos.length}</div>}
    </motion.div>
  );
}

function ShowingRequestDialog({ open, onClose, slug, agentName, address }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    setError("");
    try {
      await api.post(`/public/listing/${slug}/showing-request`, form);
      setState("success");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
      setState("idle");
    }
  };

  const close = () => { onClose(); setTimeout(() => { setState("idle"); setForm({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", message: "" }); }, 200); };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent data-testid="public-listing-showing-dialog" className="bg-[#0C1222] border border-[#C5A059]/20 text-white rounded-2xl max-w-md">
        {state === "success" ? (
          <div className="py-6 text-center" data-testid="public-listing-showing-success">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#C5A059]/15 grid place-items-center"><Check size={22} className="text-[#C5A059]" /></div>
            <h3 className="mt-4 font-serif-display text-xl">Request sent</h3>
            <p className="mt-2 text-sm text-white/60">{agentName ? `${agentName.split(" ")[0]} will` : "The listing agent will"} be in touch to confirm a time.</p>
            <Button data-testid="public-listing-showing-done" onClick={close} className="mt-6 rounded-full bg-[#C5A059] text-[#0B0F1A] hover:bg-[#d4b370]">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl font-normal">Schedule a Showing</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-white/50 -mt-2">{address}</p>
            <form onSubmit={submit} className="space-y-3.5 mt-1">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/50">Your name</Label>
                <Input data-testid="public-listing-showing-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/50">Email</Label>
                  <Input data-testid="public-listing-showing-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 rounded-lg" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/50">Phone</Label>
                  <Input data-testid="public-listing-showing-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/50">Preferred date</Label>
                  <Input data-testid="public-listing-showing-date" type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-1.5 bg-white/5 border-white/15 text-white rounded-lg [color-scheme:dark]" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/50">Preferred time</Label>
                  <Input data-testid="public-listing-showing-time" type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="mt-1.5 bg-white/5 border-white/15 text-white rounded-lg [color-scheme:dark]" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/50">Message (optional)</Label>
                <Textarea data-testid="public-listing-showing-message" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30 rounded-lg" />
              </div>
              {error && <p data-testid="public-listing-showing-error" className="text-xs text-red-400">{error}</p>}
              <p className="text-[11px] text-white/40 leading-relaxed">This is a request only — your preferred time isn't guaranteed until the agent confirms availability.</p>
              <Button data-testid="public-listing-showing-submit" type="submit" disabled={state === "submitting"} className="w-full rounded-full bg-[#C5A059] text-[#0B0F1A] hover:bg-[#d4b370] font-semibold">{state === "submitting" ? "Sending…" : "Request Showing"}</Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TiltPhoto({ src, onClick, testId, index }) {
  const ref = useRef(null);
  const [transform, setTransform] = useState("perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)");

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTransform(`perspective(900px) rotateX(${(-py * 10).toFixed(2)}deg) rotateY(${(px * 10).toFixed(2)}deg) scale3d(1.05,1.05,1.05)`);
  };
  const reset = () => setTransform("perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)");

  return (
    <motion.button ref={ref} type="button" data-testid={testId} onClick={onClick} onMouseMove={handleMove} onMouseLeave={reset}
      initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: (index % 6) * 0.07 }}
      style={{ transform, transition: "transform 0.25s ease-out" }}
      className="overflow-hidden rounded-xl bg-white/5 ring-1 ring-white/10 hover:ring-[#C5A059]/50">
      <img src={src} alt="" className="aspect-[4/3] w-full object-cover pointer-events-none" />
    </motion.button>
  );
}
